import { formatMoney, parseMoney } from '@ifixit/helpers';
import { LocaleCode } from '@ifixit/i18n';
import { ProductSearchResultItem } from '../types';

export function getHitPriceTier(
   hit: ProductSearchResultItem,
   priceTier: string | null
): { min: number; max: number } | null {
   if (!hit.price_tiers || !priceTier || !hit.price_tiers[priceTier]) {
      return null;
   }
   return hit.price_tiers[priceTier];
}

export function getHitGroupPricing(
   hit: ProductSearchResultItem
): { min: number; max: number } | null {
   if (hit.group_min_price === undefined || hit.group_max_price === undefined) {
      return null;
   }
   return {
      min: convertCentsToDecimal(hit.group_min_price),
      max: convertCentsToDecimal(hit.group_max_price),
   };
}

export function convertCentsToDecimal(cents: number): number {
   return cents / 100;
}

export function formatPriceRange(
   minPrice: number,
   maxPrice: number,
   currency: string,
   localeCodeOrUrlPrefix: LocaleCode | string
): string {
   if (minPrice === maxPrice) {
      return formatMoney(parseMoney(minPrice, currency), localeCodeOrUrlPrefix);
   }
   if (minPrice === 0) {
      return formatMoney(parseMoney(maxPrice, currency), localeCodeOrUrlPrefix);
   }
   const formattedMin = formatMoney(parseMoney(minPrice, currency), localeCodeOrUrlPrefix);
   const formattedMax = formatMoney(parseMoney(maxPrice, currency), localeCodeOrUrlPrefix);
   return `${formattedMin} - ${formattedMax}`;
}

export function determineProductPrice(
   hit: ProductSearchResultItem,
   priceTier: string | null,
   currency: string,
   localeCodeOrUrlPrefix: LocaleCode | string
): string {
   const tierPricing = getHitPriceTier(hit, priceTier);
   if (tierPricing) {
      return formatPriceRange(tierPricing.min, tierPricing.max, currency, localeCodeOrUrlPrefix);
   }

   const groupPricing = getHitGroupPricing(hit);
   if (groupPricing) {
      return formatPriceRange(groupPricing.min, groupPricing.max, currency, localeCodeOrUrlPrefix);
   }

   if (hit.price_float !== undefined) {
      return formatMoney(parseMoney(hit.price_float, currency), localeCodeOrUrlPrefix);
   }

   return hit.price || '';
}

export function determineCompareAtPrice(
   hit: ProductSearchResultItem,
   priceTier: string | null,
   currency: string,
   localeCodeOrUrlPrefix: LocaleCode | string
): string | null {
   const tierPricing = getHitPriceTier(hit, priceTier);
   let userPrice: number | undefined;
   if (tierPricing) {
      userPrice = tierPricing.min;
   } else {
      const groupPricing = getHitGroupPricing(hit);
      if (groupPricing) {
         userPrice = groupPricing.min;
      } else if (hit.price_float !== undefined) {
         userPrice = hit.price_float;
      }
   }
   const baseCompareAt = hit.compare_at_price ?? hit.price_float;
   if (baseCompareAt !== undefined && userPrice !== undefined && baseCompareAt > userPrice) {
      return formatMoney(parseMoney(baseCompareAt, currency), localeCodeOrUrlPrefix);
   }
   return null;
}
